import { useEffect, useLayoutEffect, useState } from "react";
import { translateLegacyText } from "../i18n/legacyTranslation";

const TRANSLATABLE_ATTRIBUTES = ["aria-label", "placeholder", "title"];
const textState = new WeakMap();
const attributeState = new WeakMap();

const preserveWhitespace = (source, translated) => {
  const leading = source.match(/^\s*/)?.[0] || "";
  const trailing = source.match(/\s*$/)?.[0] || "";
  return `${leading}${translated}${trailing}`;
};

const excluded = (node) => {
  const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  return Boolean(
    element?.closest?.(
      "script, style, textarea, [contenteditable='true'], [data-no-translate]"
    )
  );
};

function localizeTextNode(node, greek, catalog) {
  if (!node.nodeValue || excluded(node)) return;
  const current = node.nodeValue;
  const previous = textState.get(node);

  if (!greek) {
    if (previous && current === previous.translated) node.nodeValue = previous.source;
    textState.delete(node);
    return;
  }

  const source = previous && current === previous.translated ? previous.source : current;
  const translatedCore = translateLegacyText(source, catalog);
  if (translatedCore === source) return;
  const translated = preserveWhitespace(source, translatedCore);
  textState.set(node, { source, translated });
  if (current !== translated) node.nodeValue = translated;
}

function localizeAttribute(element, name, greek, catalog) {
  if (excluded(element) || !element.hasAttribute(name)) return;
  const current = element.getAttribute(name);
  let states = attributeState.get(element);
  if (!states) {
    states = new Map();
    attributeState.set(element, states);
  }
  const previous = states.get(name);

  if (!greek) {
    if (previous && current === previous.translated) element.setAttribute(name, previous.source);
    states.delete(name);
    return;
  }

  const source = previous && current === previous.translated ? previous.source : current;
  const translated = translateLegacyText(source, catalog);
  if (translated === source) return;
  states.set(name, { source, translated });
  if (current !== translated) element.setAttribute(name, translated);
}

function localizeTree(root, greek, catalog) {
  if (!root || excluded(root)) return;
  if (root.nodeType === Node.TEXT_NODE) {
    localizeTextNode(root, greek, catalog);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;

  if (root.nodeType === Node.ELEMENT_NODE) {
    TRANSLATABLE_ATTRIBUTES.forEach((name) => localizeAttribute(root, name, greek, catalog));
  }

  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT
  );
  let node = walker.nextNode();
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) localizeTextNode(node, greek, catalog);
    else {
      for (const name of TRANSLATABLE_ATTRIBUTES) {
        localizeAttribute(node, name, greek, catalog);
      }
    }
    node = walker.nextNode();
  }
}

export default function LegacyLocalizationBridge({ language }) {
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    let active = true;
    if (language === "el" && !catalog) {
      import("../i18n/legacyGreekCatalog.json").then((module) => {
        if (active) setCatalog(module.default || module);
      });
    }
    return () => { active = false; };
  }, [catalog, language]);

  useLayoutEffect(() => {
    // Wait for the on-demand catalog before applying Greek. English
    // restoration does not need it.
    if (language === "el" && !catalog) return undefined;
    const greek = language === "el";
    const translations = catalog || {};
    localizeTree(document.body, greek, translations);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "characterData") {
          localizeTextNode(mutation.target, greek, translations);
          return;
        }
        if (mutation.type === "attributes") {
          localizeAttribute(mutation.target, mutation.attributeName, greek, translations);
          return;
        }
        mutation.addedNodes.forEach((node) => localizeTree(node, greek, translations));
      });
    });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: TRANSLATABLE_ATTRIBUTES,
    });
    return () => observer.disconnect();
  }, [catalog, language]);

  return null;
}
